import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  TitledText,
} from '@/components';
import {
  formatOwnershipRatioFraction,
  formatOwnershipRatioPartsOfFraction,
  formatOwnershipRatioPercentage,
} from '@/features/mold/utils';

import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import {
  DiscriminatedInspectionEvaluationModel,
  ExistenceOrAbsenceLabelModifier,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const PropsModel = ProjectModel.pick({
  sheetType: true,
  fiscalYear: true,
  inspectionEvaluation: true,
});

type Props = z.infer<typeof PropsModel>;

export const OwnershipRatioInformation: FC<Props> = (props) => {
  const { t } = useTranslation();
  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(props);
  const { id: projectId } = useId();

  if (!parsed.success) {
    return null;
  }

  if (parsed.data.sheetType === 'KS') {
    const { ownershipRatio, lowUsedFundInvestigation } =
      parsed.data.inspectionEvaluation;

    return (
      <>
        <ContentWrapper title={t('ownership-ratio')}>
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              mode="fillLeft"
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="ownership-ratio"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.ownership-ratio',
                    },
                  })}
                >
                  <TitledText
                    title={t('ownership-ratio')}
                    text={
                      ownershipRatio?.operationType === 'auto'
                        ? formatOwnershipRatioFraction(
                            ownershipRatio.ratioNumerator,
                            ownershipRatio.ratioDenominator,
                          )
                        : formatOwnershipRatioPercentage(
                            ownershipRatio?.manualRatio,
                          )
                    }
                  />
                </PinpointMarker>,
                ...(ownershipRatio?.operationType === 'auto'
                  ? [
                      <PinpointMarker
                        key="ownership-ratio-numerator"
                        pinpoint={getPinpointSceneAndFieldBySheetType({
                          pinpointSheetType: 'KS_STANDARD',
                          ksStandard: {
                            path: { project: projectId },
                            field: 'ks.standard.ownership-ratio-numerator',
                          },
                        })}
                      >
                        <TitledText
                          title={t('ownership-ratio-numerator')}
                          text={formatOwnershipRatioPartsOfFraction(
                            ownershipRatio.ratioNumerator,
                          )}
                        />
                      </PinpointMarker>,
                      <PinpointMarker
                        key="ownership-ratio-denominator"
                        pinpoint={getPinpointSceneAndFieldBySheetType({
                          pinpointSheetType: 'KS_STANDARD',
                          ksStandard: {
                            path: { project: projectId },
                            field: 'ks.standard.ownership-ratio-denominator',
                          },
                        })}
                      >
                        <TitledText
                          title={t('ownership-ratio-denominator')}
                          text={formatOwnershipRatioPartsOfFraction(
                            ownershipRatio.ratioDenominator,
                          )}
                        />
                      </PinpointMarker>,
                    ]
                  : []),
              ]}
            />
            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="contents-of-held-fund-amount-of-ownership-ratio"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.contents-of-held-fund-amount-of-ownership-ratio',
                    },
                  })}
                >
                  <TitledText
                    title={t('contents-of-held-fund-amount-of-ownership-ratio')}
                    text={ownershipRatio?.basis?.contentsOfHeldFundAmount}
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="contents-of-fund-project-costs-of-ownership-ratio"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.contents-of-fund-project-costs-of-ownership-ratio',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'contents-of-fund-project-costs-of-ownership-ratio',
                    )}
                    text={ownershipRatio?.basis?.contentsOfFundProjectCosts}
                  />
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="concept-of-contents-of-each-term"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.concept-of-contents-of-each-term',
                    },
                  })}
                >
                  <TitledText
                    title={t('concept-of-contents-of-each-term')}
                    text={ownershipRatio?.conceptOfProjectProspect?.contents}
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="basis-for-estimating-indicators-for-project-prospect"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.basis-for-estimating-indicators-for-project-prospect',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'basis-for-estimating-indicators-for-project-prospect',
                    )}
                    text={
                      ownershipRatio?.conceptOfProjectProspect
                        ?.basisForCalculationOfIndicators
                    }
                  />
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              items={[
                <PinpointMarker
                  key="indicators-for-project-prospect-and-recent-performance"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.indicators-for-project-prospect-and-recent-performance',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'indicators-for-project-prospect-and-recent-performance',
                    )}
                    text={
                      ownershipRatio?.conceptOfProjectProspect
                        ?.moreRecentResults
                    }
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>
        </ContentWrapper>

        <ContentWrapper
          title={t('low-used-fund-investigation')}
          borderBottom={0}
        >
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="has-ceased-operations"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.has-ceased-operations',
                    },
                  })}
                >
                  <TitledText
                    title={t('has-ceased-operations')}
                    text={
                      typeof lowUsedFundInvestigation?.hasCeasedOperations ===
                      'boolean'
                        ? lowUsedFundInvestigation.hasCeasedOperations
                          ? ExistenceOrAbsenceLabelModifier.true
                          : ExistenceOrAbsenceLabelModifier.false
                        : null
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="has-no-operation-fund-detected"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.has-no-operation-fund-detected',
                    },
                  })}
                >
                  <TitledText
                    title={t('has-no-operation-fund-detected')}
                    text={
                      typeof lowUsedFundInvestigation?.hasNoResultRecent ===
                      'boolean'
                        ? lowUsedFundInvestigation.hasNoResultRecent
                          ? ExistenceOrAbsenceLabelModifier.true
                          : ExistenceOrAbsenceLabelModifier.false
                        : null
                    }
                  />
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="has-fund-that-lost-purpose"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.has-fund-that-lost-purpose',
                    },
                  })}
                >
                  <TitledText
                    title={t('has-fund-that-lost-purpose')}
                    text={
                      typeof lowUsedFundInvestigation?.hasFundThatLostPurpose ===
                      'boolean'
                        ? lowUsedFundInvestigation.hasFundThatLostPurpose
                          ? ExistenceOrAbsenceLabelModifier.true
                          : ExistenceOrAbsenceLabelModifier.false
                        : null
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="has-exceed-1-significantly"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.has-exceed-1-significantly',
                    },
                  })}
                >
                  <TitledText
                    title={t('has-exceed-1-significantly')}
                    text={
                      typeof lowUsedFundInvestigation?.hasExceedOneSignificantly ===
                      'boolean'
                        ? lowUsedFundInvestigation.hasExceedOneSignificantly
                          ? ExistenceOrAbsenceLabelModifier.true
                          : ExistenceOrAbsenceLabelModifier.false
                        : null
                    }
                  />
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="has-deemed-unlikely-to-be-used"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field: 'ks.standard.has-deemed-unlikely-to-be-used',
                    },
                  })}
                >
                  <TitledText
                    title={t('has-deemed-unlikely-to-be-used')}
                    text={
                      typeof lowUsedFundInvestigation?.hasDeemedUnlikelyToBeUsed ===
                      'boolean'
                        ? lowUsedFundInvestigation.hasDeemedUnlikelyToBeUsed
                          ? ExistenceOrAbsenceLabelModifier.true
                          : ExistenceOrAbsenceLabelModifier.false
                        : null
                    }
                  />
                  ,
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              style={{ marginBottom: 24 }}
              items={[
                <PinpointMarker
                  key="reason-for-selecting-absense-when-ownership-ratio-exceeds-1"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.reasons-for-selecting-absense-when-ownership-ratio-exceeds-1',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'reason-for-selecting-absense-when-ownership-ratio-exceeds-1',
                    )}
                    text={
                      lowUsedFundInvestigation?.reasonsForBeingFalseWhileExceedingOne
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="consideration-where-the-fund-are-unlikely-to-be-used"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.consideration-where-the-fund-are-unlikely-to-be-used',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'consideration-where-the-fund-are-unlikely-to-be-used',
                    )}
                    text={lowUsedFundInvestigation?.resultsOfInvestigations}
                  />
                </PinpointMarker>,
              ]}
            />

            <ContentItems
              items={[
                <PinpointMarker
                  key="reason-for-leaving-the-fund-that-are-unlikely-to-be-used"
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: 'KS_STANDARD',
                    ksStandard: {
                      path: { project: projectId },
                      field:
                        'ks.standard.reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
                    },
                  })}
                >
                  <TitledText
                    title={t(
                      'reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
                    )}
                    text={lowUsedFundInvestigation?.reasonsForLeaving}
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      </>
    );
  }
};
